
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Search, BookOpen, Layout, BarChart3, Users, Target, Clock, Layers, Sparkles, Star } from "lucide-react";
import { Topics } from "@/components/topic/Topics";
import { EducationalDashboard } from "@/components/educational/EducationalDashboard";
import { UserProgressSummary } from "@/components/dashboard/UserProgressSummary";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { Progress } from "@/components/ui/progress";
import { useUserProgress } from "@/hooks/use-user-progress";

/**
 * LearnDashboard component - Enhanced learning interface with vibrant Centse brand colors,
 * glassmorphism effects, modern aesthetic, and gamified user progress tracking.
 */
const LearnDashboard = () => {
  // State management for search functionality
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  // Get user progress data for calculating completion percentages
  const { data: userProgress } = useUserProgress();

  // Fetch learning paths with retry logic
  const {
    data: learningPaths,
    error: learningPathsError
  } = useQuery({
    queryKey: ['learningPaths'],
    queryFn: async () => {
      try {
        console.log("Fetching learning paths");
        const {
          data,
          error
        } = await supabase.from('learning_paths').select('*').eq('status', 'active').order('order_index');
        if (error) {
          console.error("Error fetching learning paths:", error);
          throw error;
        }
        return data;
      } catch (error) {
        console.error("Error in learning paths query:", error);
        toast.error("Failed to load learning paths. Please try again.");
        throw error;
      }
    },
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000)
  });

  // Fetch platform-wide statistics for the main page
  const {
    data: platformStats
  } = useQuery({
    queryKey: ['platformStatistics'],
    queryFn: async () => {
      console.log("Fetching platform-wide statistics");

      // Get all platform data in parallel
      const [topicsResult, modulesResult, lessonsResult] = await Promise.all([supabase.from('topics').select('id').eq('status', 'active'), supabase.from('modules').select('id').eq('status', 'active'), supabase.from('lessons').select('id').eq('status', 'active')]);
      if (topicsResult.error) {
        console.error("Error fetching topics:", topicsResult.error);
        throw topicsResult.error;
      }
      if (modulesResult.error) {
        console.error("Error fetching modules:", modulesResult.error);
        throw modulesResult.error;
      }
      if (lessonsResult.error) {
        console.error("Error fetching lessons:", lessonsResult.error);
        throw lessonsResult.error;
      }
      const stats = {
        totalTopics: topicsResult.data?.length || 0,
        totalModules: modulesResult.data?.length || 0,
        totalLessons: lessonsResult.data?.length || 0
      };
      console.log("Platform statistics:", stats);
      return stats;
    }
  });

  // Handle topic selection - navigate to topics page
  const handleSelectTopic = (topicId: string) => {
    console.log("🎯 Navigating to topic:", topicId);
    navigate(`/learn/topic/${topicId}`);
  };

  // Calculate progress percentages based on user progress
  const getProgressStats = () => {
    if (!userProgress || !platformStats) {
      return {
        topicsProgress: 0,
        modulesProgress: 0,
        lessonsProgress: 0
      };
    }

    const topicsProgress = platformStats.totalTopics > 0 
      ? Math.round((userProgress.completedTopics / platformStats.totalTopics) * 100)
      : 0;
    
    // For modules and lessons, we'll use a simplified calculation based on completed lessons
    const modulesProgress = Math.min(
      Math.round((userProgress.completedLessons / Math.max(platformStats.totalModules, 1)) * 100),
      100
    );
    
    const lessonsProgress = platformStats.totalLessons > 0
      ? Math.round((userProgress.completedLessons / platformStats.totalLessons) * 100)
      : 0;

    return {
      topicsProgress,
      modulesProgress,
      lessonsProgress
    };
  };

  const progressStats = getProgressStats();

  // Show error states in UI
  if (learningPathsError) {
    return <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
        <p className="text-red-600">There was an error loading the content.</p>
        <button onClick={() => window.location.reload()} className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors">
          Retry
        </button>
      </div>;
  }

  return <div className="space-y-8 animate-fade-in">
      {/* Enhanced Centse-Branded Hero Section with brand colors and playful elements */}
      <div className="flex flex-col space-y-8">
        {/* Centse-Branded Main Title */}
        <div className="text-center relative">
          {/* Centse-Branded Header */}
          <div className="relative flex items-center justify-center gap-3 mb-4">
            <h1 className="text-6xl font-black bg-gradient-to-r from-primary via-info via-secondary to-primary bg-clip-text text-transparent animate-gradient bg-[length:200%_200%]">
              Let's Make Centse
            </h1>
          </div>
          
          <p className="text-lg text-gray-600 font-bold">Turn money stress into money confidence 💪✨</p>
        </div>
        
        {/* Enhanced Search Bar with proper mobile responsiveness */}
        <div className="relative w-full mx-auto px-3 sm:px-4 sm:max-w-2xl">
          <div className="relative bg-white/80 backdrop-blur-xl border border-white/20 rounded-2xl p-1">
            <div className="relative">
              <div className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                <Search className="text-gray-400 h-4 w-4 sm:h-5 sm:w-5" />
                <Sparkles className="text-secondary h-3 w-3 sm:h-4 sm:w-4 animate-pulse" />
              </div>
              <Input 
                placeholder="What do you want to make Centse of? 🤔💡" 
                value={searchQuery} 
                onChange={e => setSearchQuery(e.target.value)} 
                className="pl-12 sm:pl-16 pr-3 sm:pr-4 py-3 sm:py-4 bg-transparent border-0 text-gray-900 placeholder:text-gray-500 focus:ring-0 text-sm sm:text-lg w-full min-w-0" 
              />
            </div>
          </div>
        </div>

        {/* NEW: User Progress Summary - Gamified learning journey tracker */}
        <UserProgressSummary />

        {/* Enhanced Platform Statistics with progress bars and Centse brand colors */}
        <div className="relative">
          <div className="relative bg-white/70 backdrop-blur-xl border border-white/30 rounded-[2.5rem] p-8 shadow-2xl">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-black bg-gradient-to-r from-primary via-secondary to-info bg-clip-text text-transparent mb-3">
                Your Centse Mission
              </h2>
              <p className="text-gray-600 font-medium">Unlock financial superpowers with our content library</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Topics Statistics - enhanced with progress using brand colors */}
              <div className="relative group">
                <div className="relative bg-white/90 backdrop-blur-xl rounded-2xl p-6 border border-white/40 hover:border-primary/50 transition-all duration-300 hover:scale-105">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-4 bg-gradient-to-br from-primary via-secondary to-primary rounded-xl shadow-lg">
                      <Target className="h-8 w-8 text-white animate-pulse" />
                    </div>
                    <div className="flex-1">
                      <p className="text-4xl font-black text-gray-900">{platformStats?.totalTopics || 0}</p>
                      <p className="text-sm font-bold text-primary uppercase tracking-wide">Topics to Master 🎯</p>
                    </div>
                  </div>
                  
                  {/* Progress section */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-gray-500 font-medium">Your Progress</span>
                      <span className="font-bold text-primary">
                        {userProgress?.completedTopics || 0}/{platformStats?.totalTopics || 0}
                      </span>
                    </div>
                    <Progress 
                      value={progressStats.topicsProgress} 
                      className="h-2"
                    />
                    <p className="text-xs text-gray-500">
                      {progressStats.topicsProgress}% unlocked
                    </p>
                  </div>
                </div>
              </div>

              {/* Modules Statistics - enhanced with progress using brand colors */}
              <div className="relative group">
                <div className="relative bg-white/90 backdrop-blur-xl rounded-2xl p-6 border border-white/40 hover:border-secondary/50 transition-all duration-300 hover:scale-105">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-4 bg-gradient-to-br from-secondary via-primary to-info rounded-xl shadow-lg">
                      <Layers className="h-8 w-8 text-white animate-bounce" style={{ animationDuration: '2s' }} />
                    </div>
                    <div className="flex-1">
                      <p className="text-4xl font-black text-gray-900">{platformStats?.totalModules || 0}</p>
                      <p className="text-sm font-bold text-secondary uppercase tracking-wide">Learning Modules 📚</p>
                    </div>
                  </div>
                  
                  {/* Progress section */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-gray-500 font-medium">Completion Rate</span>
                      <span className="font-bold text-secondary">
                        {progressStats.modulesProgress}%
                      </span>
                    </div>
                    <Progress 
                      value={progressStats.modulesProgress} 
                      className="h-2"
                    />
                    <p className="text-xs text-gray-500">
                      Keep learning to unlock more!
                    </p>
                  </div>
                </div>
              </div>

              {/* Lessons Statistics - enhanced with progress using brand colors */}
              <div className="relative group">
                <div className="relative bg-white/90 backdrop-blur-xl rounded-2xl p-6 border border-white/40 hover:border-info/50 transition-all duration-300 hover:scale-105">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-4 bg-gradient-to-br from-info via-primary to-secondary rounded-xl shadow-lg">
                      <BookOpen className="h-8 w-8 text-white animate-pulse" style={{ animationDelay: '0.5s' }} />
                    </div>
                    <div className="flex-1">
                      <p className="text-4xl font-black text-gray-900">{platformStats?.totalLessons || 0}</p>
                      <p className="text-sm font-bold text-info uppercase tracking-wide">Epic Lessons ⚡</p>
                    </div>
                  </div>
                  
                  {/* Progress section */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-gray-500 font-medium">Completed</span>
                      <span className="font-bold text-info">
                        {userProgress?.completedLessons || 0}/{platformStats?.totalLessons || 0}
                      </span>
                    </div>
                    <Progress 
                      value={progressStats.lessonsProgress} 
                      className="h-2"
                    />
                    <p className="text-xs text-gray-500">
                      {progressStats.lessonsProgress}% mastered
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Main Content Tabs with Centse brand styling */}
      <Tabs defaultValue="categories" className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-8 bg-white/80 backdrop-blur-xl border border-white/30 rounded-2xl p-2 shadow-lg">
          <TabsTrigger value="categories" className="flex items-center gap-2 rounded-xl data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary data-[state=active]:via-secondary data-[state=active]:to-primary data-[state=active]:text-white font-bold transition-all duration-300">
            <Layout className="h-4 w-4" />
            <span className="hidden sm:inline">Categories 📁</span>
          </TabsTrigger>
          <TabsTrigger value="paths" className="flex items-center gap-2 rounded-xl data-[state=active]:bg-gradient-to-r data-[state=active]:from-secondary data-[state=active]:via-info data-[state=active]:to-primary data-[state=active]:text-white font-bold transition-all duration-300">
            <BookOpen className="h-4 w-4" />
            <span className="hidden sm:inline">Learning Paths 🛤️</span>
          </TabsTrigger>
          <TabsTrigger value="dashboard" className="flex items-center gap-2 rounded-xl data-[state=active]:bg-gradient-to-r data-[state=active]:from-info data-[state=active]:via-primary data-[state=active]:to-secondary data-[state=active]:text-white font-bold transition-all duration-300">
            <BarChart3 className="h-4 w-4" />
            <span className="hidden sm:inline">Dashboard 📈</span>
          </TabsTrigger>
        </TabsList>

        {/* Categories Tab with enhanced Topics component */}
        <TabsContent value="categories">
          <Topics onSelectTopic={handleSelectTopic} />
        </TabsContent>

        {/* Learning Paths Tab - updated with Centse brand colors */}
        <TabsContent value="paths" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {learningPaths?.map((path, index) => <Card key={path.id} className="cursor-pointer hover:shadow-2xl transition-all duration-300 bg-white/80 backdrop-blur-xl border border-white/30 rounded-2xl hover:scale-105 group relative overflow-hidden">
                <CardHeader className="relative z-10">
                  <CardTitle className="group-hover:bg-gradient-to-r group-hover:from-primary group-hover:via-secondary group-hover:to-info group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">
                    {path.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="relative z-10">
                  <p className="text-sm text-gray-600 mb-4">{path.description}</p>
                  <div className="mt-4">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold text-white ${index % 4 === 0 ? 'bg-gradient-to-r from-primary to-secondary' : index % 4 === 1 ? 'bg-gradient-to-r from-secondary to-info' : index % 4 === 2 ? 'bg-gradient-to-r from-info to-primary' : 'bg-gradient-to-r from-primary to-info'}`}>
                      {path.difficulty || 'Beginner'} ⭐
                    </span>
                  </div>
                </CardContent>
              </Card>)}
          </div>
        </TabsContent>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard">
          <EducationalDashboard />
        </TabsContent>
      </Tabs>
    </div>;
};

export default LearnDashboard;
